import { forwardRef } from "react";
import styled from "@emotion/styled";
import type { ListPageLayoutProps } from "./ListPageLayout.types";

/* Spacing and typography follow the Shiprocket 4.0 Agentic list-page spec
 * (Figma node 1680:36874, Orders): 20px page gutter, header band 12px/10px
 * vertical padding with a strong bottom border, 28px-tall filter controls
 * vertically centred in their band, 20px bottom margin under the table. */

const ListPageLayoutRoot = styled("div", {
  label: "AgenticListPageLayout-root"
})({
  display: "flex",
  minWidth: "0px",
  minHeight: "100%",
  flexDirection: "column",
  // The root owns the page sheet color; inner strips (tabs list, FilterBar)
  // are set transparent below so the whole layout reads as one surface.
  backgroundColor: "#FBFCFB"
});

const ListPageLayoutHeader = styled("div", {
  label: "AgenticListPageLayout-header"
})({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: ".75rem",
  padding: ".75rem 1.25rem .625rem"
});

const ListPageLayoutTitle = styled("h1", {
  label: "AgenticListPageLayout-title"
})({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "20px",
  fontWeight: 600,
  lineHeight: "1.5rem",
  color: "hsl(var(--bfrs-color-text-primary))"
});

const ListPageLayoutActions = styled("div", {
  label: "AgenticListPageLayout-actions"
})({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: ".5rem"
});

const ListPageLayoutKpis = styled("div", {
  label: "AgenticListPageLayout-kpis"
})({
  display: "flex",
  alignItems: "stretch",
  flexWrap: "wrap",
  gap: ".75rem",
  padding: ".75rem 1.25rem 0",
  // Cards share the band's full width equally; the min width lets rows wrap
  // on narrow screens instead of crushing the cards.
  "& > *": {
    flex: "1 1 0%",
    minWidth: "10rem"
  }
});

const ListPageLayoutBannerAboveTabs = styled("div", {
  label: "AgenticListPageLayout-bannerAboveTabs"
})({
  padding: ".75rem 1.25rem 0"
});

const ListPageLayoutBanner = styled("div", {
  label: "AgenticListPageLayout-banner"
})({
  padding: ".75rem 1.25rem 0"
});

const ListPageLayoutBannerBelowFilters = styled("div", {
  label: "AgenticListPageLayout-bannerBelowFilters"
})({
  padding: ".75rem 1.25rem 0"
});

const ListPageLayoutTabs = styled("div", {
  label: "AgenticListPageLayout-tabs"
})({
  // No horizontal gutter here: the line-variant Tabs list carries its own
  // 1.25rem padding, so adding the band gutter would double-indent the labels.
  // With the list spanning the full band its divider needs no bleed either.
  padding: ".5rem 0 0",
  "--bfrs-tabs-divider-bleed": "0px",
  // The tab strip doesn't paint its own surface — the layout root's sheet
  // color shows through, whatever it is.
  "--bfrs-tabs-list-bg": "transparent"
});

const ListPageLayoutFilters = styled("div", {
  label: "AgenticListPageLayout-filters"
})({
  display: "flex",
  alignItems: "center",
  flexWrap: "nowrap",
  gap: ".75rem",
  padding: ".625rem 1.25rem",
  // Match the line-variant Tabs divider so the tabs and filter band read as one
  // continuous list-page separator.
  borderBottomWidth: "1px",
  borderBottomStyle: "solid",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  // A slotted FilterBar shouldn't paint its own surface either (see root).
  "--bfrs-filterbar-bg": "transparent",
  // Filters stay on one line per the list-page spec; overflow scrolls instead
  // of wrapping, and controls keep their intrinsic width.
  overflowX: "auto",
  "& > *": {
    flexShrink: 0
  },
  // A single composite child (e.g. FilterBar) spans the whole band so its
  // right-aligned actions reach the band edge; loose controls keep their
  // intrinsic widths.
  "& > *:only-child": {
    flexGrow: 1
  }
});

const ListPageLayoutTable = styled("div", {
  label: "AgenticListPageLayout-table"
})({
  minWidth: "0px",
  minHeight: "0px",
  display: "flex",
  flexDirection: "column",
  flex: "1 1 auto",
  padding: ".75rem 1.25rem 1.25rem",
  "--bfrs-data-table-flex": "1 1 auto",
  "& > *": {
    minWidth: "0px",
    minHeight: "0px",
    display: "flex",
    flex: "1 1 auto",
    flexDirection: "column"
  }
});

export const ListPageLayout = forwardRef<HTMLDivElement, ListPageLayoutProps>(
  (
    {
      className,
      title,
      actions,
      kpis,
      bannerAboveTabs,
      banner,
      bannerBelowFilters,
      tabs,
      filters,
      table,
      headerClassName,
      titleClassName,
      actionsClassName,
      kpisClassName,
      bannerAboveTabsClassName,
      bannerClassName,
      bannerBelowFiltersClassName,
      tabsClassName,
      filtersClassName,
      tableClassName,
      ...props
    },
    ref
  ) => (
    <ListPageLayoutRoot ref={ref} className={className} {...props}>
      <ListPageLayoutHeader className={headerClassName}>
        <ListPageLayoutTitle className={titleClassName}>{title}</ListPageLayoutTitle>
        {actions && <ListPageLayoutActions className={actionsClassName}>{actions}</ListPageLayoutActions>}
      </ListPageLayoutHeader>
      {kpis && <ListPageLayoutKpis className={kpisClassName}>{kpis}</ListPageLayoutKpis>}
      {bannerAboveTabs && (
        <ListPageLayoutBannerAboveTabs className={bannerAboveTabsClassName}>{bannerAboveTabs}</ListPageLayoutBannerAboveTabs>
      )}
      {tabs && <ListPageLayoutTabs className={tabsClassName}>{tabs}</ListPageLayoutTabs>}
      {banner && <ListPageLayoutBanner className={bannerClassName}>{banner}</ListPageLayoutBanner>}
      {filters && <ListPageLayoutFilters className={filtersClassName}>{filters}</ListPageLayoutFilters>}
      {bannerBelowFilters && (
        <ListPageLayoutBannerBelowFilters className={bannerBelowFiltersClassName}>{bannerBelowFilters}</ListPageLayoutBannerBelowFilters>
      )}
      {table && <ListPageLayoutTable className={tableClassName}>{table}</ListPageLayoutTable>}
    </ListPageLayoutRoot>
  )
);

ListPageLayout.displayName = "ListPageLayout";
